import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Box, Divider, Drawer, List, ListItemButton, ListItemIcon, ListItemText, Theme, useMediaQuery } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Observation } from '../../../shared/types';
import { ObservationData } from './ObservationData';

const MIN_DRAWER_WIDTH = 300;
const MAX_DRAWER_WIDTH_PERCENT = 0.85;
const DEFAULT_DRAWER_WIDTH_PERCENT = 0.6;

type Props = {
    observations: Observation[];
};

export const ObservationsList = ({ observations }: Props) => {
    const [selectedObservation, setSelectedObservation] = useState<Observation | null>(null);
    const isNotSm = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'));
    const [drawerWidth, setDrawerWidth] = useState(() => window.innerWidth * DEFAULT_DRAWER_WIDTH_PERCENT);
    const isDragging = useRef(false);

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        isDragging.current = true;
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';

        const handleMouseMove = (e: MouseEvent) => {
            if (!isDragging.current) return;
            const newWidth = window.innerWidth - e.clientX;
            const maxWidth = window.innerWidth * MAX_DRAWER_WIDTH_PERCENT;
            setDrawerWidth(Math.max(MIN_DRAWER_WIDTH, Math.min(newWidth, maxWidth)));
        };

        const handleMouseUp = () => {
            isDragging.current = false;
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }, []);

    const handleSelectObservation = (observation: Observation) => {
        setSelectedObservation(observation);
    };

    const handleCloseObservationDrawer = () => {
        setSelectedObservation(null);
    };

    useEffect(() => {
        if (isNotSm && !selectedObservation) {
            setSelectedObservation(observations[0]);
        }
    }, [selectedObservation, isNotSm]);

    useEffect(() => {
        const found = observations.find(({ _id }) => selectedObservation?._id === _id);

        if (found) setSelectedObservation(found);
    }, [observations]);

    return (
        <Box sx={{ height: '100%', overflow: 'hidden', display: 'flex' }}>
            <List
                sx={{
                    width: isNotSm ? `calc(100% - ${drawerWidth}px)` : '100%',
                    minWidth: isNotSm ? 200 : undefined,
                    height: '100%',
                    overflowY: 'scroll',
                    p: 0,
                }}>
                {observations.map((observation) => {
                    const {
                        station: { _id, name, description },
                    } = observation;
                    const selected = selectedObservation?.station._id === _id;

                    return (
                        <ListItemButton key={_id.toString()} divider selected={selected} onClick={() => handleSelectObservation(observation)}>
                            <ListItemText primary={`${name} - ${description}`} />
                            {selected && (
                                <ListItemIcon>
                                    <NavigateNextIcon />
                                </ListItemIcon>
                            )}
                        </ListItemButton>
                    );
                })}
            </List>
            {selectedObservation && (
                <Drawer
                    variant={isNotSm ? 'permanent' : 'temporary'}
                    anchor="right"
                    open={Boolean(selectedObservation)}
                    hideBackdrop={true}
                    sx={{ '& .MuiDrawer-paper': { width: isNotSm ? drawerWidth : '100%' } }}
                    slotProps={{
                        paper: { sx: (theme) => ({ backgroundColor: theme.palette.background.paper, top: isNotSm ? 156 : undefined, pb: isNotSm ? '156px' : undefined }) },
                    }}>
                    {isNotSm && (
                        <Box
                            onMouseDown={handleMouseDown}
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                bottom: 0,
                                width: 6,
                                cursor: 'col-resize',
                                zIndex: 1,
                                '&:hover': { backgroundColor: 'primary.main', opacity: 0.3 },
                            }}
                        />
                    )}
                    <ObservationData observation={selectedObservation} onClose={handleCloseObservationDrawer} showClose={!isNotSm} />
                </Drawer>
            )}
        </Box>
    );
};
